import { useCartQuery } from '@graphql/queries/__generated__/cart.generated'
import { useMeQuery } from '@graphql/queries/__generated__/me.generated'
import CloseIcon from '@mui/icons-material/Close'
import { Divider } from '@mui/material'
import Grid from '@mui/material/Grid'
import Hidden from '@mui/material/Hidden'
import Cookies from 'js-cookie'
import { useSnackbar } from 'notistack'
import qs from 'qs'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQueryParams } from 'use-query-params'
import SearchIcon from '../../../icons/SearchIcon'
import useLocalStorage from '../../hooks/useLocalStorage'
import { ScrollDirection } from '../../hooks/useScrollDirection'
import { SEARCH_URL } from '../../urls'
import { GlobalDialogFormsParams } from '../GlobalDialogForms'

import HeaderLogo from './components/HeaderLogo'
import HeaderMobileSearch from './components/HeaderMobileSearch'
import HeaderUtilities from './components/HeaderUtilities'
import Menu from './Menu/Menu'
import { headerOffsetAnimatedVar, headerOffsetVar, isWholesaleModeVar } from '../../../apolloClient'
import { COOKIE_NAMES, LOCAL_STORAGE_KEYS } from '../../../constants'
import { useReactiveVar } from '@apollo/client'
import { CartTypeEnum } from '../../../generated/gqlTypes'
import HeaderSearch from '@components/Header/components/HeaderSearch'
import {
  BACKEND_ENUM_TO_IMG_SIZE,
  DEFAULT_IMG_SIZE,
  REFLECTIONS_IMG_SIZE_ENUM,
  STORAGE_GRID_NAME,
} from '../../Pages/Category/SpecificsList'

const MobileMenu = React.lazy(() => import('./Menu/MobileMenu'))

// Mirrors the sticky-header heights and the scroll-down "hide" offset in
// _header.scss (.header--sticky, .header--hidden) - kept here so the space
// reserved below the header (.header--placeholder + headerOffsetVar, read by
// sticky sidebars) always matches what's actually visible, in every scroll state.
const MD_SCREEN = 899
const STICKY_HEIGHT = { mobile: 56, desktop: 100 }
const STICKY_HEIGHT_WHOLESALE = { mobile: 101, desktop: 125 }
const HIDDEN_OFFSET = { mobile: 60, desktop: 70 }
const HIDDEN_OFFSET_WHOLESALE_MOBILE = 101
// Between md and lg the header already uses the mobile layout (burger, Menu.tsx's desktop
// category row is hidden), but keeps the desktop-height top row - so the sticky height is
// the desktop one minus the missing category row.
const LG_SCREEN = 1199
const MENU_ROW_HEIGHT = 30
// ENTER/EXIT are deliberately different (hysteresis): a single hard threshold flickers
// on/off from ordinary sub-pixel scroll jitter whenever the scroll position sits still
// right on top of it - that flicker was visible as the right-hand sticky panel jumping.
const SCROLL_HIDE_ENTER_PX = 200
const SCROLL_HIDE_EXIT_PX = 140
// Same reasoning applies one level up, to the plain "sticky vs not sticky" collapse
// (header--sticky, ~100px tall vs its natural in-flow size): a bare `scrollY > 0` has
// no dead zone, so rubber-band/overscroll bounce at the very top of the page - which
// briefly wobbles scrollY around 0 - flips it repeatedly, jumping the header itself
// between its two sizes and, through headerOffsetVar, the right-hand sticky panel.
const STICKY_ENTER_PX = 5
const STICKY_EXIT_PX = 0
// Minimum scroll distance before the scroll direction is considered changed.
const SCROLL_DIRECTION_THRESHOLD_PX = 50
// Gap left between the sticky header and a block scrolled to by scrollToBlockTop.
const SCROLL_TO_BLOCK_GAP_PX = 20

const getStickyHeaderHeight = () => {
  const stickyHeights = isWholesaleModeVar() ? STICKY_HEIGHT_WHOLESALE : STICKY_HEIGHT
  if (window.innerWidth <= MD_SCREEN) return stickyHeights.mobile
  return window.innerWidth <= LG_SCREEN ? stickyHeights.desktop - MENU_ROW_HEIGHT : stickyHeights.desktop
}

// Smoothly scrolls up so the top of `element` sits right below the sticky header, but only if that
// top is currently scrolled out of view (e.g. after clicking a pagination control at the bottom of a
// long list). Scrolling up always un-hides the header, so its full sticky height is reserved rather
// than the current (possibly hidden) headerOffsetVar.
export const scrollToBlockTop = (element: HTMLElement | null) => {
  if (!element) return
  const offset = getStickyHeaderHeight() + SCROLL_TO_BLOCK_GAP_PX
  const elementTop = element.getBoundingClientRect().top
  if (elementTop >= offset) return
  window.scrollTo({ top: window.scrollY + elementTop - offset, behavior: 'smooth' })
}

const HeaderBase = () => {
  const history = useHistory()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const [{ isSticky, isHidden, animateReservedHeight }, setScrollState] = useState({
    isSticky: false,
    isHidden: false,
    animateReservedHeight: false,
  })
  const [, setParams] = useQueryParams(GlobalDialogFormsParams)
  const parsed = qs.parse(history.location.search, {
    ignoreQueryPrefix: true,
  })
  const [isActiveOverlay, setIsActiveOverlay] = useState(false)
  const [, setSearchActive, getSearchActive] = useLocalStorage<boolean>(LOCAL_STORAGE_KEYS.SEARCH_ACTIVE, false)
  const isWholesaleMode = useReactiveVar(isWholesaleModeVar)
  const [, forceResizeRecompute] = useState(0)
  const cartQuery = useCartQuery({
    context: { notBatching: true },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: { cartType: isWholesaleMode ? CartTypeEnum.Wholesale : CartTypeEnum.Default, includeTotalPrice: false },
  })

  const { data: userData, loading: userLoading } = useMeQuery()
  const [searchData, setSearchData] = React.useState((parsed && parsed.q) || '')
  const [, setGridClass] = useLocalStorage<REFLECTIONS_IMG_SIZE_ENUM>(STORAGE_GRID_NAME, DEFAULT_IMG_SIZE)

  useEffect(() => {
    if (userData?.me?.pk) {
      Cookies.remove(COOKIE_NAMES.CART)
    } else {
      if (cartQuery.data?.cart) {
        Cookies.set(COOKIE_NAMES.CART, cartQuery.data?.cart.cookieToken, { sameSite: 'lax' })
      }
    }
  }, [cartQuery.data?.cart, userData?.me])
  useEffect(() => {
    if (!!parsed && !!parsed.q) {
      setSearchActive(!!parsed && !!parsed.q)
    }
  }, [])

  useEffect(() => {
    if (window.dataLayer && !userLoading) {
      window.dataLayer.push({
        event: 'set-user-email',
        email: userData?.me?.email || '',
      })
    }
  }, [userData])

  useEffect(() => {
    if (!userLoading && !userData?.me) {
      isWholesaleModeVar(false)
      localStorage.removeItem(LOCAL_STORAGE_KEYS.WHOLESALE_MODE)
    }
  }, [userData, userLoading])

  useEffect(() => {
    if (userData?.me?.productListImageSize && window.localStorage.getItem(STORAGE_GRID_NAME) === null) {
      setGridClass(BACKEND_ENUM_TO_IMG_SIZE[userData.me.productListImageSize])
    }
  }, [userData?.me?.productListImageSize])

  useEffect(() => {
    const q = (parsed && parsed.q) || ''
    if (!q) {
      setSearchData('')
    }
    setSearchActive(false)
  }, [location.pathname])

  const onSubmitSearch = (data: { q: string; categoryName: string; categorySlug: string; categoryId: number }) => {
    setSearchData(data.q)
    const search = qs.stringify(
      { q: data.q, categories: data.categoryId ? [`${data.categorySlug}_${data.categoryId}`] : [] },
      {
        addQueryPrefix: true,
        arrayFormat: 'repeat',
      }
    )
    setSearchActive(false)
    history.push({
      pathname: SEARCH_URL,
      search,
    })
  }

  const changeStatusSearchBar = () => {
    setSearchActive(!getSearchActive())
  }

  const handleClickBag = e => {
    e.preventDefault()
    enqueueSnackbar('Your bag is empty', {
      variant: 'info',
    })
  }
  const handleOpenSignInDialog = e => {
    e.preventDefault()
    setParams({ global_dialog_form: 'signIn' }, 'replaceIn')
  }
  const handleClickWSMode = () => {
    isWholesaleModeVar(!isWholesaleMode)
    if (!isWholesaleMode) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.WHOLESALE_MODE, 'true')
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.WHOLESALE_MODE)
    }
  }
  // Scroll is tracked outside React and only the derived sticky/hidden flags go into state,
  // so the header re-renders only when one of them actually flips. Keeping the raw scrollY
  // in state re-rendered the whole header (menu, search, utilities) on every scroll frame,
  // which during fast scrolling dropped frames and made the header's slide stutter.
  useEffect(() => {
    let lastDirectionY = Math.max(window.scrollY, 0)
    let scrollDir = ScrollDirection.UP
    let frame = 0

    const update = () => {
      frame = 0
      const scrollY = Math.max(window.scrollY, 0)
      if (Math.abs(scrollY - lastDirectionY) >= SCROLL_DIRECTION_THRESHOLD_PX) {
        scrollDir = scrollY > lastDirectionY ? ScrollDirection.DOWN : ScrollDirection.UP
        lastDirectionY = scrollY
      }
      setScrollState(prev => {
        const isSticky = scrollY > (prev.isSticky ? STICKY_EXIT_PX : STICKY_ENTER_PX)
        // Once hidden, require scrolling back up past the (lower) exit threshold rather
        // than the (higher) enter one - and vice versa. A single shared threshold flickers
        // on/off from ordinary sub-pixel scroll jitter whenever the scroll position sits
        // still right on top of it, which showed up as the right-hand sticky panel jumping.
        const isHidden =
          isSticky &&
          scrollDir === ScrollDirection.DOWN &&
          scrollY > (prev.isHidden ? SCROLL_HIDE_EXIT_PX : SCROLL_HIDE_ENTER_PX)
        if (isSticky === prev.isSticky && isHidden === prev.isHidden) return prev
        // Entering/leaving sticky mode swaps the header between position:relative (natural,
        // larger size) and position:fixed (compact) - neither is animatable, so it's an
        // instant cut on the header's own side. Only animate the panel offset for changes
        // that happen *within* sticky mode (the hidden/shown slide, which the header itself
        // does animate via its `top` transition).
        return { isSticky, isHidden, animateReservedHeight: isSticky && prev.isSticky }
      })
    }
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.cancelAnimationFrame(frame)
    }
  }, [])

  useEffect(() => {
    const onResize = () => forceResizeRecompute(n => n + 1)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Space kept in the document flow below the fixed header. It must stay constant while
  // the header slides hidden/shown: shrinking it shifts the whole page by the hidden
  // offset mid-scroll, the browser's scroll anchoring compensates by moving scrollY by
  // the same amount, the direction tracking above reads that as a scroll in the opposite
  // direction and toggles the header back - an endless loop, visible as the header
  // jumping during fast scrolling.
  let placeholderHeight = 0
  // Height of the header part actually visible on screen - only used to offset sticky
  // panels (headerOffsetVar), which are positioned and don't affect the page layout.
  let reservedHeight = 0
  if (isSticky) {
    const isMobile = window.innerWidth <= MD_SCREEN
    const stickyHeight = getStickyHeaderHeight()
    const hiddenOffsetFull = isMobile && isWholesaleMode ? HIDDEN_OFFSET_WHOLESALE_MOBILE : isMobile ? HIDDEN_OFFSET.mobile : HIDDEN_OFFSET.desktop
    placeholderHeight = stickyHeight
    reservedHeight = Math.max(stickyHeight - (isHidden ? hiddenOffsetFull : 0), 0)
  }

  useEffect(() => {
    headerOffsetVar(reservedHeight)
    headerOffsetAnimatedVar(animateReservedHeight)
  }, [reservedHeight, animateReservedHeight])

  return (
    <>
      <header className={`header w-full${isSticky ? ' header--sticky' : ''}${isHidden ? ' header--hidden' : ''}`}>
        {/*<PromoCodes hide={isSticky || isWholesaleMode} />*/}
        <div
          onClick={() => setIsActiveOverlay(false)}
          className={`wrapOverlayGlobal ${isActiveOverlay ? 'active' : ''}`}
        />
        <div className='relative' style={{ zIndex: 500 }}>
          <div className={` bg-white z-50`}>
            <div className='container'>
              {/*<Container maxWidth='xl' disableGutters>*/}
              <div className='mx-auto' style={{ maxWidth: '1680px' }}>
                <Grid container spacing={1}>
                  <Grid item>
                    <div className='flex items-center h-full z-10 relative'>
                      <Hidden lgUp>
                        <React.Suspense
                          fallback={
                            <div id='nav-icon'>
                              <span />
                              <span />
                              <span />
                              <span />
                            </div>
                          }
                        >
                          <MobileMenu />
                        </React.Suspense>
                        {getSearchActive() ? (
                          <CloseIcon onClick={changeStatusSearchBar} className='ml-20' />
                        ) : (
                          <SearchIcon onClick={changeStatusSearchBar} className='ml-20' />
                        )}
                      </Hidden>
                      <Hidden lgDown>
                        <HeaderLogo />
                      </Hidden>
                    </div>
                  </Grid>

                  <Grid item flexGrow={1}>
                    <Hidden lgUp>
                      <div className='flex h-full items-center'>
                        <HeaderLogo />
                      </div>
                    </Hidden>
                    <Hidden lgDown>
                      <div className='h-full items-center'>
                        <HeaderSearch onSubmitSearch={onSubmitSearch} initialQuery={(searchData as string) || ''} />
                      </div>
                    </Hidden>
                  </Grid>
                  <Grid item>
                    <div className={`header-utilities bg-white h-full relative z-10`}>
                      <HeaderUtilities
                        user={userData?.me}
                        cartCount={cartQuery.data?.cart?.count || 0}
                        handleClickSignIn={handleOpenSignInDialog}
                        isStaff={userData?.me?.isStaff}
                        handleClickBag={handleClickBag}
                        setIsActiveOverlay={setIsActiveOverlay}
                        handleClickContactUs={() => setParams({ global_dialog_form: 'contactUs' })}
                        handleClickWSMode={handleClickWSMode}
                        isWholesaleMode={isWholesaleMode}
                      />
                    </div>
                  </Grid>
                </Grid>
              </div>
              {/*</Container>*/}

              <HeaderMobileSearch
                onSubmitSearch={onSubmitSearch}
                isActive={getSearchActive()}
                initialQuery={(searchData as string) || ''}
                handleToggle={changeStatusSearchBar}
              />
              <Divider />
              <Menu setIsActiveOverlay={setIsActiveOverlay} />
            </div>
          </div>
        </div>
      </header>
      <div
        className='header--placeholder'
        style={{
          height: placeholderHeight,
          overflowAnchor: 'none',
        }}
      />
    </>
  )
}

export default React.memo(HeaderBase)
